import { useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/notifications';
import { subscribeForeground, syncToken } from '@/lib/webPushController';

const TAG = 'admin/web-push-bootstrap';

/**
 * Componente "invisível" montado uma vez no shell do admin: garante o token
 * FCM do navegador e mostra toast para push recebido em foreground.
 *
 * Origem: sem issue — commit 0cda1b9f9, 2026-05-12 (web push FCM — service
 * worker, controller/hook e card de notificações).
 *
 * Contrato: no mount chama `syncToken()` (quando a preferência está ligada —
 * opt-out, default ligado; se a permissão ainda é `default`, pede ao
 * navegador), que registra/renova o token pelas callables
 * `registerAdminPushToken`/`unregisterAdminPushToken`. Assina
 * `subscribeForeground` e transforma cada mensagem em toast (Flashbar). Falha
 * do `syncToken` só vai para `console.warn`. Não renderiza nada.
 *
 * Usado por: `main.tsx` (fora do router, dentro do `ErrorBoundary`). A
 * preferência é ligada/desligada em `features/settings/NotificationsCard.tsx`
 * (`/settings`).
 *
 * Arquitetura: quem envia é o servidor — `sendWebPushToAdmins`
 * (`apps/functions/src/lib/webPush.ts`) no lead de contato
 * (`onContactSubmissionCreated`) e o `notifyAdmins`
 * (`apps/functions/src/lib/adminNotify.ts`), chamado por billing, webhooks,
 * `onTenantCreated` e outros; as preferências por evento ficam em
 * `/settings/notifications` (`NotificationPrefsPage`).
 *
 * Histórico:
 *  - 2026-10-10 13:37 +0200 — (#926) removida a frase "hoje nenhum evento faz
 *    fan-out para super-admins", falsa desde 2026-06-18 (`09af09e86`, push do
 *    lead de contato) e 2026-06-19 (`6d8cac847`, `notifyAdmins`).
 */
export function WebPushBootstrap(): JSX.Element | null {
  const { t } = useTranslation();

  useEffect(() => {
    void syncToken().catch((err: unknown) => {
      console.warn(`${TAG}: syncToken falhou`, err);
    });
  }, []);

  useEffect(() => {
    let unsub = (): void => {
      /* substituído após a assinatura resolver */
    };
    let cancelled = false;
    void subscribeForeground((payload) => {
      const notif = payload.notification;
      const title = notif?.title ?? t('admin.settings.notifications.toastTitle');
      const body = notif?.body ?? '';
      if (body) toast(title, { description: body });
      else toast(title);
    }).then((u): void => {
      if (cancelled) u();
      else unsub = u;
    });
    return (): void => {
      cancelled = true;
      unsub();
    };
  }, [t]);

  return null;
}
